import instantiator from '../../model/util/instantiator';
import type { INSTANTIATOR_INTERFACES } from '../../model/interface/instantiator';
import type { IWorkspaceTable } from '../interface/table';
import { Table } from 'dexie';

/* Api data from serve response for type of Interface */
type ApiData<Interface extends keyof INSTANTIATOR_INTERFACES> = Parameters<
    INSTANTIATOR_INTERFACES[Interface]
>[0];

class ContainerTable<
    Interface extends keyof INSTANTIATOR_INTERFACES,
    Constructor extends INSTANTIATOR_INTERFACES[Interface],
    Entity extends ReturnType<Constructor>,
> implements IWorkspaceTable<Interface, Constructor, Entity>
{
    private _table: Table<ApiData<Interface>, any>;
    private _entityName: Interface;

    constructor(options: {
        table: Table<ApiData<Interface>, any>;
        name: keyof INSTANTIATOR_INTERFACES;
    }) {
        this._table = options.table;
        this._entityName = options.name as Interface;
    }

    count(): Promise<number> {
        return this._table.count();
    }

    first(): Promise<Entity> {
        const table = this._table;
        const entityName = this._entityName;
        return new Promise<Entity>(function (resolve, reject) {
            table
                .toArray()
                .then((arr: Array<ApiData<Interface>>) => {
                    if (arr.length === 0) {
                        reject('No items found');
                    }
                    const item =
                        instantiator.instantiate(entityName, arr[0]) ?? (null as Entity | null);
                    item
                        ? resolve(item as Entity)
                        : reject('Failed to instantiate element ' + entityName);
                })
                .catch((err: Error) => reject(err));
        });
    }

    get(pk: any): Promise<Entity> {
        const table = this._table;
        const entityName = this._entityName;
        return new Promise<Entity>(function (resolve, reject) {
            table
                .get(pk)
                .then(data => {
                    if (data) {
                        const item = instantiator.instantiate(entityName, data ?? {});
                        resolve(item as Entity);
                    } else {
                        reject('Unable to instantiate item for pk:' + pk);
                    }
                })
                .catch((err: Error) => reject(err));
        });
    }

    all(): Promise<Entity[]> {
        const table = this._table;
        const entityName = this._entityName;
        return new Promise<Entity[]>(function (resolve, reject) {
            table
                .toArray()
                .then((arr: Array<ApiData<Interface>>) => {
                    const items: Entity[] = [];
                    arr.forEach(x => {
                        const item = instantiator.instantiate(entityName, x);
                        if (item) {
                            items.push(item as Entity);
                        }
                    });
                    resolve(items);
                })
                .catch((err: Error) => reject(err));
        });
    }

    bulkAddOrUpdate(items: Array<Entity>): Promise<void> {
        const table = this._table;
        return new Promise<void>(function (resolve, reject) {
            table
                .bulkPut(items, { allKeys: false })
                .then(() => resolve())
                .catch((err: Error) => {
                    reject(err);
                });
        });
    }
}

function ctor<
    Interface extends keyof INSTANTIATOR_INTERFACES,
    Constructor extends INSTANTIATOR_INTERFACES[Interface],
    Entity extends ReturnType<Constructor>,
>(
    name: Interface,
    table: Table<ApiData<Interface>, any>,
): IWorkspaceTable<Interface, Constructor, Entity> {
    const obj = new ContainerTable({
        name,
        table,
    });

    return obj as IWorkspaceTable<Interface, Constructor, Entity>;
}

export default ctor;
